import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Layout, Space, Typography, Form, Input, Steps, message } from "antd";
import { MailOutlined, LockOutlined, SafetyOutlined } from "@ant-design/icons";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { AppLogo } from "@/Assets/Index";

const { Text, Title } = Typography;
const { Content, Header } = Layout;

export const ForgotPassword = () => {
  const fetch = useAuthenticatedFetch();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // Step 1: Send OTP to email
  const handleSendOTP = async (values) => {
    setLoading(true);
    try {
      const res = await fetch.post("/reset", { email: values.email });
      if (res.success || res.data) {
        setEmail(values.email);
        setCurrentStep(1);
        message.success("OTP sent to your email!");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (values) => {
    setLoading(true);
    try {
      const res = await fetch.post("/verify-otp", { email, otp: values.otp });
      if (res.success || res.data?.verified) {
        setCurrentStep(2);
        message.success("OTP verified successfully!");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (values) => {
    if (values.password !== values.confirmPassword) {
      message.error("Passwords do not match!");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch.post("/password", {
        email,
        password: values.password,
        confirmPassword: values.confirmPassword
      });
      if (res.success || res.data) {
        message.success("Password reset successfully!");
        navigate("/app/login");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const res = await fetch.post("/reset", { email });
      if (res.success || res.data) {
        message.success("New OTP sent to your email!");
      }
    } catch (error) {
      message.error("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { title: "Email", icon: <MailOutlined /> },
    { title: "Verify OTP", icon: <SafetyOutlined /> },
    { title: "New Password", icon: <LockOutlined /> }
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSendOTP}
            size="large"
            requiredMark={(label, { required }) => (
              <>
                {label}
                {required && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
              </>
            )}
          >
            <Form.Item
              label="Email Address"
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email address" }
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Enter your registered email"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  width: "100%",
                  height: "44px",
                  borderRadius: "8px",
                  border: "none"
                }}
              >
                {loading ? "Sending OTP..." : "Send OTP"}
              </Button>
            </Form.Item>
          </Form>
        );

      case 1:
        return (
          <Form
            layout="vertical"
            onFinish={handleVerifyOTP}
            size="large"
            requiredMark={(label, { required }) => (
              <>
                {label}
                {required && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
              </>
            )}
          >
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <Text type="secondary">
                We've sent a 6-digit OTP to <strong>{email}</strong>
              </Text>
            </div>

            <Form.Item
              label="Enter OTP"
              name="otp"
              rules={[
                { required: true, message: "OTP is required" },
                { pattern: /^\d{6}$/, message: "OTP must be 6 digits" }
              ]}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                style={{ textAlign: "center", letterSpacing: "8px", fontSize: "18px" }}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  width: "100%",
                  height: "44px",
                  borderRadius: "8px",
                  border: "none"
                }}
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </Button>
            </Form.Item>

            <div style={{ textAlign: "center" }}>
              <Text type="secondary">Didn't receive the code? </Text>
              <Button
                type="link"
                onClick={handleResendOTP}
                disabled={loading}
                style={{ padding: 0 }}
              >
                Resend OTP
              </Button>
            </div>

            <div style={{ textAlign: "center", marginTop: "10px" }}>
              <Button
                type="link"
                onClick={() => setCurrentStep(0)}
                style={{ padding: 0 }}
              >
                Change Email
              </Button>
            </div>
          </Form>
        );

      case 2:
        return (
          <Form
            layout="vertical"
            onFinish={handleResetPassword}
            size="large"
            requiredMark={(label, { required }) => (
              <>
                {label}
                {required && <span style={{ color: "#ff4d4f", marginLeft: 4 }}>*</span>}
              </>
            )}
          >
            <Form.Item
              label="New Password"
              name="password"
              rules={[
                { required: true, message: "Password is required" },
                { min: 8, message: "Password must be at least 8 characters" },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                  message: "Password must contain uppercase, lowercase, number, and special character (@$!%*?&)"
                }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter new password"
              />
            </Form.Item>

            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm new password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  width: "100%",
                  height: "44px",
                  borderRadius: "8px",
                  border: "none"
                }}
              >
                {loading ? "Resetting Password..." : "Reset Password"}
              </Button>
            </Form.Item>
          </Form>
        );

      default:
        return null;
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f5f5", margin: 0, padding: 0 }}>
      <style>{`
        body { margin: 0 !important; padding: 0 !important; }
        .ant-layout { margin: 0 !important; padding: 0 !important; }
      `}</style>
      <Header
        style={{
          background: "#fff",
          padding: "0 24px",
          height: "64px",
          display: "flex",
          alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <img src={AppLogo} alt="Upresume.ai" style={{ height: "40px", cursor: "pointer" }} onClick={() => navigate("/")} />
      </Header>
      <Content
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 64px)",
          padding: "24px",
          margin: 0,
        }}
      >
        <div style={{ width: "100%", maxWidth: "450px" }}>
          <Card style={{ borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div style={{ textAlign: "center" }}>
                <Title level={2} style={{ color: "#1f2937", marginBottom: "8px" }}>
                  Reset Password
                </Title>
                <Text type="secondary">
                  {currentStep === 0 && "Enter your email to receive a password reset OTP"}
                  {currentStep === 1 && "Enter the OTP sent to your email"}
                  {currentStep === 2 && "Create a new password for your account"}
                </Text>
              </div>

              <Steps
                current={currentStep}
                items={steps}
                size="small"
                style={{ marginBottom: "24px" }}
              />

              {renderStepContent()}

              <div style={{ textAlign: "center" }}>
                <Text style={{ color: "#6b7280" }}>
                  Remember your password?{" "}
                  <Button
                    type="link"
                    onClick={() => navigate("/app/login")}
                    style={{ padding: 0, fontWeight: "600" }}
                  >
                    Sign in here
                  </Button>
                </Text>
              </div>
            </Space>
          </Card>
        </div>
      </Content>
    </Layout>
  );
};
